import { Button } from "@/components/ui/button";
import { getCategoryDisplay, getFrequencyDisplay } from "../../util";

import { Body } from "@hypercard/web-components";
import { Card } from "@/components/ui/card";
import { AnimatedProps, animated } from "@react-spring/web";
import { CSSProperties } from "react";
import { Separator } from "@/components/ui/separator";
import { formatDollars } from "@common/components/utils";
import { getDepartmentDisplay } from "../../util";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";
import { useRecoilValue } from "recoil";
import {
  StipendRulesDocument,
  useCreateStipendRuleMutation,
  useUpdateStipendRuleMutation,
} from "@common/utils/graphql";
import { useToast } from "@/components/ui/use-toast";
import { endOfDay, startOfDay } from "date-fns";
import { CircleNotch } from "@phosphor-icons/react";

type Props = AnimatedProps<{
  onNext: () => void;
  onBack: () => void;
  style: CSSProperties;
  isEditing: boolean;
}>;

export const Confirmation = ({ onNext, onBack, style, isEditing }: Props) => {
  const stipend = useRecoilValue(createOrEditStipendState);
  const { toast } = useToast();
  const [createStipend, { loading: loadingUpdate }] =
    useCreateStipendRuleMutation({
      refetchQueries: [StipendRulesDocument],
    });
  const [updateStipend, { loading: loadingCreate }] =
    useUpdateStipendRuleMutation({
      refetchQueries: [StipendRulesDocument],
    });
  const loading = loadingUpdate || loadingCreate;
  const handleSave = async () => {
    const {
      name,
      terms,
      startsAt,
      expiresAt,
      longDetails,
      departments,
      frequency,
      amount,
      id,
      category,
    } = stipend;
    const action = id ? "updated" : "created";
    try {
      let departmentIds: number[] | null = departments
        .map((d) => d.id)
        .filter((id) => id !== null) as number[];
      if (departmentIds.length === 0) {
        departmentIds = null;
      }
      if (isEditing) {
        const { data } = await updateStipend({
          variables: {
            input: {
              departmentIds,
              name,
              terms,
              longDetails,
              category,
              expiresAt: expiresAt ? endOfDay(expiresAt!).toISOString() : null,
            },
            stipendRuleId: id!,
          },
        });
        if (
          data?.updateStipendRule.__typename ===
          "MutationUpdateStipendRuleSuccess"
        ) {
          toast({
            title: `Stipend ${action} successfully`,
          });
          onNext();
        } else {
          throw new Error();
        }
      } else {
        const { data } = await createStipend({
          variables: {
            input: {
              name: name,
              terms,
              longDetails: longDetails,
              amount: Number(amount),
              departmentIds,
              frequency: frequency!,
              startsAt: startOfDay(startsAt!).toISOString(),
              expiresAt: expiresAt ? endOfDay(expiresAt!).toISOString() : null,
              category: category as string,
            },
          },
        });
        if (
          data?.createStipendRule.__typename ===
          "MutationCreateStipendRuleSuccess"
        ) {
          toast({
            title: `Stipend ${action} successfully`,
          });
          onNext();
        } else {
          throw new Error();
        }
      }
    } catch (e) {
      toast({
        title: `Failed to ${action} stipend, please try again.`,
        variant: "destructive",
      });
    }
  };
  return (
    <animated.div
      style={style}
      className="mt-24 flex w-full grow flex-col items-center lg:pl-28 lg:pr-14"
    >
      <Body size="2xl" weight="medium" className="text-center">
        {isEditing ? "Update Stipend" : "Confirm new Stipend"}
      </Body>
      <Body
        size="md"
        weight="medium"
        className="mt-3 text-center text-white/70"
      >
        Verify this information is correct
      </Body>
      <div className="relative z-10 mt-16 flex overflow-hidden rounded-md bg-border p-px text-start lg:min-w-[600px]">
        <div
          className="absolute -left-24 -top-40 -z-20 size-64 rounded-full blur-3xl"
          style={{
            backgroundColor: "#5854C1",
          }}
        />
        <Card className="flex h-full grow flex-col items-start border-none p-6">
          <Body size="lg" weight="medium">
            {stipend.name}
          </Body>
          <Body size="md" weight="regular" className="mt-2 text-white/70">
            {stipend.longDetails}
          </Body>
          <Separator orientation="horizontal" className="my-4 w-full" />
          <div className="flex w-full flex-wrap gap-3">
            <div className="grow space-y-3">
              <Body size="sm" weight="regular">
                Amount
              </Body>
              <Body size="md" weight="regular">
                {formatDollars(stipend.amount)}
              </Body>
            </div>
            <div className="grow space-y-3">
              <Body size="sm" weight="regular">
                Category
              </Body>
              <Body size="md" weight="regular">
                {getCategoryDisplay(stipend.category!)}
              </Body>
            </div>
            <div className="grow space-y-3">
              <Body size="sm" weight="regular">
                Department
              </Body>
              <Body size="md" weight="regular">
                {getDepartmentDisplay(stipend.departments!)}
              </Body>
            </div>
            <div className="grow space-y-3">
              <Body size="sm" weight="regular">
                Frequency
              </Body>
              <div className="w-max rounded bg-[#1B1B1B] px-4 py-2">
                <Body size="sm" weight="medium" className="text-white/70">
                  {getFrequencyDisplay(stipend.frequency)}
                </Body>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className="mt-8 flex w-full flex-grow items-end justify-center gap-x-10">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleSave} type="submit" className="w-44">
          {loading ? (
            <CircleNotch className="size-4 animate-spin" />
          ) : isEditing ? (
            "Save Stipend"
          ) : (
            "Create Stipend"
          )}
        </Button>
      </div>
    </animated.div>
  );
};
